<div class="rounded-lg border transition-colors" [class]="containerClass()">
  <!-- Collapsed row -->
  <div class="flex items-center gap-2 px-3 py-2">
    @if (selectable()) {
      <p-checkbox
        [binary]="true"
        [ngModel]="selected()"
        (onChange)="toggleSelected()"
        [inputId]="'tip-' + tip().id"
        [ariaLabel]="subtitle() || (i18nKey() + '.title' | transloco)"
      />
    } @else {
      <span class="w-5 shrink-0"></span>
    }

    <button
      type="button"
      class="flex min-w-0 flex-1 items-center gap-3 text-left"
      [attr.aria-expanded]="expanded()"
      (click)="toggleExpanded()"
    >
      <div
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-200"
      >
        <lucide-icon [name]="icon()" [size]="16" class="text-primary-500" />
      </div>

      <span class="min-w-0 flex-1">
        @if (!nested()) {
          <span
            class="block truncate text-paragraph-s-reg font-medium text-primary-900"
          >
            {{ i18nKey() + ".title" | transloco }}
          </span>
        }
        @if (subtitle()) {
          <span
            class="block truncate"
            [class]="
              nested()
                ? 'text-paragraph-s-reg font-medium text-primary-900'
                : 'text-paragraph-xs-reg text-greyscale-600'
            "
          >
            {{ subtitle() }}
          </span>
        }
      </span>

      <!-- A committed action still carrying a tip is a commitment this trip
           breaks — it has to read as a shortfall, not as an achievement. -->
      @if (tip().isCommitted) {
        <span
          class="flex shrink-0 items-center gap-1 rounded-full bg-warning-100 px-2 py-0.5 text-paragraph-xs-reg font-medium text-warning-700"
          [pTooltip]="'reductionTips.commitments.unmetBadgeTooltip' | transloco"
          tooltipPosition="top"
        >
          <lucide-icon name="triangle-alert" [size]="12" />
          {{ "reductionTips.commitments.unmetBadge" | transloco }}
        </span>
      }

      @if (selectable()) {
        <span
          class="shrink-0 text-paragraph-s-reg font-semibold tabular-nums text-validation-600"
        >
          −{{ displaySavings() | formatCO2e }}
        </span>
      } @else {
        <span class="shrink-0 text-paragraph-xs-reg text-greyscale-500">
          {{ "reductionTips.informational" | transloco }}
        </span>
      }

      <lucide-icon
        name="chevron-down"
        [size]="16"
        class="shrink-0 text-greyscale-500 transition-transform"
        [class.rotate-180]="expanded()"
      />
    </button>
  </div>

  <!-- Expanded detail -->
  @if (expanded()) {
    <div class="border-t border-primary-100 px-3 py-3">
      @switch (tip().type) {
        @case ("transport_alternative") {
          @if (alternativeData(); as data) {
            <p class="mb-3 text-paragraph-s-reg text-greyscale-600">
              {{
                "reductionTips.transportAlternative.distance"
                  | transloco: { distance: round(data.distance) }
              }}
            </p>
            <div class="flex flex-wrap items-start gap-2">
              <div
                class="flex w-[120px] shrink-0 flex-col items-center gap-1 rounded-lg border border-transparent px-2 py-2"
              >
                <span
                  class="flex h-8 items-center justify-center gap-1 text-center text-paragraph-xs-reg font-medium leading-tight text-primary-900"
                >
                  <lucide-icon name="plane" [size]="14" class="shrink-0" />
                  {{ "reductionTips.transportAlternative.plane" | transloco }}
                </span>
                <app-chart-doughnut
                  [textSize]="'small'"
                  [data]="singleValueChart(alternativeCurrent())"
                  [centerValue]="co2eValue(alternativeCurrent())"
                  [centerLabel]="co2eUnit(alternativeCurrent())"
                  [size]="chartSize"
                  [thickness]="chartThickness"
                  [showLabelInTooltip]="false"
                />
              </div>

              @for (alt of alternatives(); track alt.mode) {
                <button
                  type="button"
                  class="flex w-[120px] shrink-0 flex-col items-center gap-1 rounded-lg border px-2 py-2 transition-colors hover:border-primary-400"
                  [class.border-primary-500]="
                    selected() && effectiveMode() === alt.mode
                  "
                  [class.bg-primary-100]="
                    selected() && effectiveMode() === alt.mode
                  "
                  [class.border-primary-200]="
                    !(selected() && effectiveMode() === alt.mode)
                  "
                  (click)="selectAlternative(alt.mode)"
                >
                  <span
                    class="flex h-8 items-center justify-center gap-1 text-center text-paragraph-xs-reg font-medium leading-tight text-primary-900"
                  >
                    <lucide-icon
                      [name]="alt.icon"
                      [size]="14"
                      class="shrink-0"
                    />
                    {{ alt.labelKey | transloco }}
                  </span>
                  <app-chart-doughnut
                    [textSize]="'small'"
                    [data]="alt.chart"
                    [centerValue]="co2eValue(alt.emissions)"
                    [centerLabel]="co2eUnit(alt.emissions)"
                    [size]="chartSize"
                    [thickness]="chartThickness"
                    [showLabelInTooltip]="false"
                  />
                </button>
              }
            </div>
            <p class="mt-2 text-paragraph-xs-reg text-greyscale-500">
              {{ "reductionTips.alternatives.choose" | transloco }}
            </p>
          }
        }

        @case ("direct_flight") {
          @if (directFlightValues(); as values) {
            <p class="mb-3 text-paragraph-s-reg text-greyscale-600">
              {{ "reductionTips.directFlight.description" | transloco }}
              {{
                "reductionTips.directFlight.savings"
                  | transloco
                    : {
                        savings: co2e(values.savings),
                        percent: values.savingsPercent,
                      }
              }}
            </p>
            <div class="flex flex-wrap items-start gap-2">
              <div
                class="flex w-[120px] shrink-0 flex-col items-center gap-1 rounded-lg border border-transparent px-2 py-2"
              >
                <span
                  class="flex h-8 items-center justify-center gap-1 text-center text-paragraph-xs-reg font-medium leading-tight text-primary-900"
                >
                  <lucide-icon name="plane" [size]="14" class="shrink-0" />
                  {{ "reductionTips.directFlight.multiStop" | transloco }}
                </span>
                <app-chart-doughnut
                  [textSize]="'small'"
                  [data]="singleValueChart(values.current)"
                  [centerValue]="co2eValue(values.current)"
                  [centerLabel]="co2eUnit(values.current)"
                  [size]="chartSize"
                  [thickness]="chartThickness"
                  [showLabelInTooltip]="false"
                />
              </div>
              <div
                class="flex w-[120px] shrink-0 flex-col items-center gap-1 rounded-lg border border-transparent px-2 py-2"
              >
                <span
                  class="flex h-8 items-center justify-center gap-1 text-center text-paragraph-xs-reg font-medium leading-tight text-primary-900"
                >
                  <lucide-icon name="plane" [size]="14" class="shrink-0" />
                  {{ "reductionTips.directFlight.direct" | transloco }}
                </span>
                <app-chart-doughnut
                  [textSize]="'small'"
                  [data]="savingsChart(values.direct, values.savings)"
                  [centerValue]="co2eValue(values.direct)"
                  [centerLabel]="co2eUnit(values.direct)"
                  [size]="chartSize"
                  [thickness]="chartThickness"
                  [showLabelInTooltip]="false"
                />
              </div>
            </div>
          }
        }

        @case ("eco_accommodation") {
          @if (accommodationValues(); as values) {
            <p class="mb-3 text-paragraph-s-reg text-greyscale-600">
              {{
                "reductionTips.ecoAccommodation.description"
                  | transloco
                    : {
                        savings: co2e(values.savings),
                        percent: values.savingsPercent,
                      }
              }}
            </p>
            <div class="flex flex-wrap items-start gap-2">
              <div
                class="flex w-[120px] shrink-0 flex-col items-center gap-1 rounded-lg border border-transparent px-2 py-2"
              >
                <span
                  class="flex h-8 items-center justify-center gap-1 text-center text-paragraph-xs-reg font-medium leading-tight text-primary-900"
                >
                  <lucide-icon name="building" [size]="14" class="shrink-0" />
                  {{ "reductionTips.ecoAccommodation.current" | transloco }}
                </span>
                <app-chart-doughnut
                  [textSize]="'small'"
                  [data]="singleValueChart(values.current)"
                  [centerValue]="co2eValue(values.current)"
                  [centerLabel]="co2eUnit(values.current)"
                  [size]="chartSize"
                  [thickness]="chartThickness"
                  [showLabelInTooltip]="false"
                />
              </div>
              <div
                class="flex w-[120px] shrink-0 flex-col items-center gap-1 rounded-lg border border-transparent px-2 py-2"
              >
                <span
                  class="flex h-8 items-center justify-center gap-1 text-center text-paragraph-xs-reg font-medium leading-tight text-primary-900"
                >
                  <lucide-icon name="leaf" [size]="14" class="shrink-0" />
                  {{ "reductionTips.ecoAccommodation.eco" | transloco }}
                </span>
                <app-chart-doughnut
                  [textSize]="'small'"
                  [data]="savingsChart(values.eco, values.savings)"
                  [centerValue]="co2eValue(values.eco)"
                  [centerLabel]="co2eUnit(values.eco)"
                  [size]="chartSize"
                  [thickness]="chartThickness"
                  [showLabelInTooltip]="false"
                />
              </div>
            </div>
          }
        }

        @case ("trip_duration") {
          @if (durationData(); as data) {
            <p class="mb-3 text-paragraph-s-reg text-greyscale-600">
              {{ "reductionTips.tripDuration.description" | transloco }}
            </p>
            <p-chart
              type="bar"
              [data]="getDurationChartData(data)"
              [options]="getDurationChartOptions()"
              height="170px"
            />
            <div class="flex justify-around pl-10">
              @for (score of getDurationScores(data); track $index) {
                <app-ecoscore [ecoScore]="score" />
              }
            </div>
          }
        }

        @case ("high_emission_activity") {
          <p class="text-paragraph-s-reg text-greyscale-600">
            {{ "reductionTips.highEmissionActivity.description" | transloco }}
          </p>
        }
      }
    </div>
  }
</div>
